/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import User, {UserFields} from "../../../models/userModel";
import {ResponseData, showDialog, validateEmail} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {RegisterErrors, RegisterStep} from "../../../util/interfaces";
import {AppContext} from "../../misc/appContext";
import {onAddAccount} from "../../../util/events";
import {googleRegister} from "../../../util/requestSender";
import {setSentryUser} from "../../../util/sentryUtil";
import {sendGaEvent, sendGaPageView, signUpEvent} from "../../../util/analytics";
import Register from "./register";
import {saveToAppLocal} from "../../../util/storageUtil";
import {createErrorMessage, getFingerprint} from "../../../util/utils";
import {CredentialResponse} from '@react-oauth/google';
import {fallbackUsersEstimate, getActiveUsersEstimate} from "../../../util/statUtil";
import Invitation from "../../../models/invitationModel";
import {useNavigate} from 'react-router';
import {ORDERS_ROUTE} from "../../../util/constants";

const EMPTY_ERRORS: RegisterErrors = {
    password: null, lastname: null, firstname: null, email: null, companyName: null,
}

export default function RegisterController() {
    const context = useContext(AppContext);
    const [user, setUser] = useState(new User());
    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<RegisterErrors>(EMPTY_ERRORS);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [invitationID, setInvitationID] = useState<string | null>(null);
    const [step, setStep] = useState<RegisterStep>(RegisterStep.METHOD_CHOICE);
    const [usersEstimate, setUsersEstimate] = useState(fallbackUsersEstimate);
    const navigate = useNavigate();

    function onChangeUser(key: keyof UserFields) {
        return function(value: string) {
            setUser(user.clone().setField(key, value));
        }
    }

    function onChangePassword(password: string) {
        setPassword(password);
    }

    function onToggleTerms() {
        setTermsAccepted(!termsAccepted);
    }

    function onChangeCompanyName(value: string) {
        setCompanyName(value);
    }

    function onResetError(key: keyof RegisterErrors) {
        return function() {
            setErrors(Object.assign({}, errors, {[key]: null}));
        }
    }

    function onClickLogin() {
        sendGaEvent('Click', 'Registreren', 'Log in');
    }

    function onClickGoogle() {
        sendGaEvent('Click', 'Registreren', 'Google registreer');
        signUpEvent('Google');
    }

    function onChangeStep(step: RegisterStep) {
        return function() {
            setStep(step);
        }
    }

    function onSubmit() {
        if (!validateValues()) return;
        sendGaEvent('Click', 'Registreren', 'Registreer');
        signUpEvent('Manual');
        setLoading(true);
        const today = IDate.now();
        user.flex = {
            // We don't need companyName when the user is invited by another user, it also messes with the option
            // to add a seller account in the future
            companyName: !!invitationID ? undefined : companyName,
            lastNotifiedUpdate: today.format('YYYY-MM-DD'),
            warn_2fa_after: today.format('YYYY-MM-DD')
        };
        user.register(password, invitationID, (user: User, _: boolean, data: ResponseData) => {
            requestCallback(data, user);
        });
    }

    function onClickTrustpilot() {
        window.open('https://www.trustpilot.com/review/bolmate.nl', '_blank', 'noopener,noreferrer');
    }

    function onGoogleSuccess(response: CredentialResponse) {
        const token = response.credential;
        if (!token) return onGoogleError();
        setLoading(true);
        getFingerprint().then((fingerprint: string) => {
            googleRegister({
                data: {token, fp: fingerprint}, callback: (data: ResponseData) => {
                    requestCallback(data, null);
                }
            });
        })
    }

    function onGoogleError() {
        showDialog('Er is iets misgegaan bij het registreren met Google.', 'Registreren mislukt');
    }

    function validateValues(): boolean {
        const {firstname, lastname, email} = user;
        const _errors = Object.assign({}, errors);
        let valid = true;
        if (!firstname.length) {
            _errors.firstname = 'De voornaam mag niet leeg zijn';
            valid = false;
        }
        if (!lastname.length) {
            _errors.lastname = 'De achternaam mag niet leeg zijn';
            valid = false;
        }
        if (!email.length) {
            _errors.email = 'Het e-mailadres mag niet leeg zijn';
            valid = false;
        } else if (!validateEmail(email)) {
            _errors.email = 'Het e-mailadres is niet geldig';
            valid = false;
        }
        if (!password.length) {
            _errors.password = 'Het wachtwoord mag niet leeg zijn';
            valid = false;
        }
        if (!companyName.length) {
            _errors.companyName = 'De bedrijfsnaam mag niet leeg zijn';
            valid = false;
        }
        if (!termsAccepted) {
            showDialog('Accepteer de voorwaarden om een account te registreren');
            valid = false;
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    function requestCallback(data: ResponseData, user: User | null) {
        setLoading(false);
        if (data.success) {
            saveToAppLocal('newUser', true);
            if (!user) user = new User(data.user);
            setSentryUser(user);
            const account = user.accounts.length ? user.accounts[0] : undefined;
            context.onChangeContext({user, accounts: user.accounts, account}, () => {
                if (account) navigate(ORDERS_ROUTE);
                else onAddAccount();
            });
        } else showDialog(createErrorMessage(data.message ?? 'Het aanmaken van het account is niet gelukt.'),
            'Registratie mislukt');
    }

    function loadUsersEstimate() {
        getActiveUsersEstimate().then((estimate: number) => {
            setUsersEstimate(estimate);
        });
    }

    function loadInvitation() {
        const params = new URLSearchParams(location.search);
        if (!params.has('uitnodiging')) return;
        setLoading(true);
        const _invitationID = params.get('uitnodiging')!;
        Invitation.check(_invitationID, (invitation: Invitation | null, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                setInvitationID(invitation!.id);
                setUser(user.clone().setField('email', invitation!.email));
                setCompanyName(data.company_name);
                setStep(RegisterStep.FORM);
            } else
                showDialog(data.message ?? createErrorMessage('De uitnodiging kan niet gevonden worden.'),
                    'Uitnodiging mislukt', [{label: 'Oké', onClick: window.close}]);
        });
    }

    useEffect(() => {
        sendGaPageView('/registreren');
        loadUsersEstimate();
        loadInvitation();
    }, []);

    return (
        <Register onChangeUser={onChangeUser} onChangePassword={onChangePassword} onChangeTerms={onToggleTerms}
                  onChangeCompanyName={onChangeCompanyName} onSubmit={onSubmit} onResetError={onResetError}
                  onGoogleSuccess={onGoogleSuccess} onGoogleError={onGoogleError} onClickLogin={onClickLogin}
                  onClickGoogle={onClickGoogle} user={user} password={password} loading={loading} errors={errors}
                  termsAccepted={termsAccepted} companyName={companyName} step={step} onChangeStep={onChangeStep}
                  onClickTrustPilot={onClickTrustpilot} usersEstimate={usersEstimate}
                  isInvited={!!invitationID}/>
    )
}